import React from 'react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Edit2, Copy, Eye, ChevronDown, ChevronRight, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Heading1, Heading2, Quote, Download, FileDown, Printer, Share2, ExternalLink, Mail, MessageSquare } from 'lucide-react';

export default function TemplatesCoopersModals({ ctx }) {
  const { templates, variablesDisponibles, isEditing, setIsEditing, editingTemplate, formData, setFormData, isCustomType, setIsCustomType, customTypeValue, setCustomTypeValue, showPreview, setShowPreview, previewTemplate, variablesExpanded, setVariablesExpanded, showShareDialog, setShowShareDialog, shareTemplate, shareEmail, setShareEmail, shareMessage, setShareMessage, editorRef, insertVariable, applyFormat, insertHeading, handleGenerateDocument, handleSaveTemplate, handlePrintTemplate, handleDownloadPDF, handleShareTemplate, handleSendShare, handleCopyShareLink, openEditDialog } = ctx;
  return (
    <>
      {/* Dialog de prévisualisation */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Prévisualisation - {previewTemplate?.nom}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              Type: {previewTemplate?.type_document}
              {previewTemplate?.is_active ? (
                <Badge className="bg-green-600 text-white font-semibold">Actif</Badge>
              ) : (
                <Badge className="bg-gray-400 font-semibold">Inactif</Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {previewTemplate && (
            <div className="space-y-4">
              {previewTemplate.description && (
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-800">
                    {previewTemplate.description}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="border rounded-lg p-6 bg-white min-h-[300px]">
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewTemplate.contenu_html || '<p class="text-gray-400 italic">Aucun contenu</p>') }}
                />
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-xs text-gray-500">
                  ID: {previewTemplate.id?.substring(0, 8)}...
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrintTemplate(previewTemplate)}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimer
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPDF(previewTemplate)}
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareTemplate(previewTemplate)}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Partager
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fermer
            </Button>
            <Button onClick={() => {
              setShowPreview(false);
              openEditDialog(previewTemplate);
            }}>
              <Edit2 className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de partage */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-green-600" />
              Partager le template
            </DialogTitle>
            <DialogDescription>
              {shareTemplate?.nom}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Partage par lien */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Lien de partage
              </Label>
              <div className="flex gap-2">
                <Input 
                  value={`${window.location.origin}/templates/cooper/${shareTemplate?.id}`}
                  readOnly
                  className="text-xs"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopyShareLink}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">ou par email</span>
              </div>
            </div>
            
            {/* Partage par email */}
            <div className="space-y-2">
              <Label htmlFor="shareEmail" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Adresse email
              </Label>
              <Input 
                id="shareEmail"
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="destinataire@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="shareMessage" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Message (optionnel)
              </Label>
              <Textarea 
                id="shareMessage"
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendShare}
              className="bg-green-600 hover:bg-green-700"
            >
              <Mail className="w-4 h-4 mr-2" />
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog d'édition */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Modifier le Template' : 'Nouveau Template'}
            </DialogTitle>
            <DialogDescription>
              Créez ou modifiez votre template personnalisé avec l&apos;éditeur visuel
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Nom du template */}
            <div className="grid gap-2">
              <Label htmlFor="nom">Nom du Template</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Ex: Ma Proposition Personnalisée"
              />
            </div>

            {/* Type de document */}
            <div className="grid gap-2">
              <Label htmlFor="type">Type de Document</Label>
              <Select
                value={isCustomType ? 'custom' : formData.type_document}
                onValueChange={(value) => {
                  if (value === 'custom') {
                    setIsCustomType(true);
                    setFormData({ ...formData, type_document: 'custom' });
                  } else {
                    setIsCustomType(false);
                    setCustomTypeValue('');
                    setFormData({ ...formData, type_document: value });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="proposition">Proposition</SelectItem>
                  <SelectItem value="conditions">Conditions Générales</SelectItem>
                  <SelectItem value="attestation">Attestation</SelectItem>
                  <SelectItem value="custom">🎨 Autre (type personnalisé)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Champ pour type personnalisé */}
            {isCustomType && (
              <div className="grid gap-2 animate-in fade-in duration-200">
                <Label htmlFor="customType">Nom du Type Personnalisé</Label>
                <Input
                  id="customType"
                  value={customTypeValue}
                  onChange={(e) => setCustomTypeValue(e.target.value)}
                  placeholder="Ex: Garantie Solidaire Caution Fournisseur"
                  className="border-purple-300 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500">
                  Ce type apparaîtra dans une nouvelle section sur la page principale
                </p>
              </div>
            )}

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">Description (optionnelle)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Décrivez brièvement ce template..."
                rows={2}
              />
            </div>

            {/* Variables disponibles */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-2 text-sm">Variables Disponibles</h4>
              <p className="text-xs text-gray-600 mb-3">
                Cliquez sur une variable pour l&apos;insérer dans votre template
              </p>
              <div className="space-y-2">
                {Object.entries(variablesDisponibles).map(([category, vars]) => (
                  <div key={category} className="border-b pb-2">
                    <button
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-blue-600 w-full text-left"
                      onClick={() => setVariablesExpanded(prev => ({ ...prev, [category]: !prev[category] }))}
                    >
                      {variablesExpanded[category] ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                      <Badge className="bg-purple-600 text-white ml-auto font-semibold">{vars.length}</Badge>
                    </button>
                    {variablesExpanded[category] && (
                      <div className="flex flex-wrap gap-1 mt-2 ml-6">
                        {vars.map(variable => (
                          <Button
                            key={variable}
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => insertVariable(variable)}
                          >
                            {variable}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Éditeur WYSIWYG */}
            <div className="grid gap-2">
              <div className="flex justify-between items-center">
                <Label>Contenu du Template</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? 'Éditer' : 'Prévisualiser'}
                </Button>
              </div>
              
              {showPreview ? (
                <div
                  className="border rounded-lg p-4 min-h-[400px] bg-white prose max-w-none overflow-auto"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(formData.contenu_html) }}
                />
              ) : (
                <div>
                  {/* Barre d'outils de formatage */}
                  <div className="border border-slate-300 rounded-t-lg bg-slate-50 p-2 flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => insertHeading(1)}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Titre 1"
                    >
                      <Heading1 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => insertHeading(2)}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Titre 2"
                    >
                      <Heading2 className="h-4 w-4" />
                    </button>
                    <div className="w-px bg-slate-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => applyFormat('bold')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Gras"
                    >
                      <Bold className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('italic')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Italique"
                    >
                      <Italic className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('underline')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Souligné"
                    >
                      <Underline className="h-4 w-4" />
                    </button>
                    <div className="w-px bg-slate-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => applyFormat('justifyLeft')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Aligner à gauche"
                    >
                      <AlignLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('justifyCenter')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Centrer"
                    >
                      <AlignCenter className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('justifyRight')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Aligner à droite"
                    >
                      <AlignRight className="h-4 w-4" />
                    </button>
                    <div className="w-px bg-slate-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => applyFormat('insertUnorderedList')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Liste à puces"
                    >
                      <List className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('insertOrderedList')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Liste numérotée"
                    >
                      <ListOrdered className="h-4 w-4" />
                    </button>
                    <div className="w-px bg-slate-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Entrez l\'URL du lien:');
                        if (url) applyFormat('createLink', url);
                      }}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Insérer un lien"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('formatBlock', 'blockquote')}
                      className="p-2 hover:bg-slate-200 rounded"
                      title="Citation"
                    >
                      <Quote className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Zone d'édition */}
                  <div
                    ref={editorRef}
                    contentEditable
                    onInput={(e) => setFormData({ ...formData, contenu_html: e.currentTarget.innerHTML })}
                    onFocus={() => editorRef.current.focus()}
                    dangerouslySetInnerHTML={{ __html: formData.contenu_html }}
                    className="w-full min-h-[500px] px-4 py-3 border border-t-0 border-slate-300 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-auto"
                    style={{
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      lineHeight: '1.6'
                    }}
                  />
                  
                  <p className="text-xs text-slate-500 mt-2">
                    💡 Sélectionnez du texte et utilisez les boutons de formatage. Cliquez sur une variable pour l&apos;insérer.
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Annuler
            </Button>
            <Button 
              variant="outline" 
              onClick={handleGenerateDocument}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Générer HTML
            </Button>
            <Button onClick={handleSaveTemplate} className="bg-blue-600 hover:bg-blue-700">
              {editingTemplate ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
